import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, CheckCircle2, Copy, ShieldCheck } from 'lucide-react';
import { useSubmitBkashPayment } from '../../application/hooks/usePayments';
import { useCurrentUser } from '../../application/hooks/useCurrentUser';
import { useMySubscription } from '../../application/hooks/useSubscription';
import { BookingLockNotice } from '../components/payments/BookingLockNotice';
import { usePaymentLock } from '../../application/hooks/usePaymentLock';
import { useTranslation } from '../../application/hooks/useTranslation';
import { BKASH_PAY_MODE, BKASH_PAY_NUMBER, PAY_FALLBACK_PATH } from '../../config/features';
import { formatBDT } from '../../domain/formatters/currency';
import { errorMessageOf } from '../components/feedback/QueryState';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import './pages.css';
import './BkashPay.css';
import { todayLocal } from '../../domain/localDate';

type PayType = 'INSTALLMENT' | 'ADVANCE';
type Field = 'trxId' | 'amount' | 'senderNumber' | 'paidAt';
type FieldErrors = Partial<Record<Field, string>>;

const TRX_RE = /^[A-Z0-9]{8,12}$/;
const SENDER_RE = /^01\d{9}$/;
const FIELDS: Field[] = ['trxId', 'amount', 'senderNumber', 'paidAt'];

/** Axios problem+json → { status, code, field errors } (no request data is read). */
function problemOf(error: unknown): { status?: number; code?: string; errors: { field: string; message: string }[] } {
  const res = (error as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
  const data = res?.data ?? {};
  return {
    status: res?.status,
    code: typeof data.code === 'string' ? data.code : undefined,
    errors: Array.isArray(data.errors) ? (data.errors as { field: string; message: string }[]) : [],
  };
}

/**
 * Manual bKash payment: (1) send money to the shown number in the bKash app, (2) submit the TrxID,
 * (3) pending-verification confirmation. Created PENDING; an administrator verifies it like a bank slip.
 */
export default function BkashCheckoutPage() {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sub = useMySubscription();
  const lock = usePaymentLock();
  const submitBkash = useSubmitBkashPayment();

  const emi = sub.data?.monthlyAmount ?? 0;
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [payType, setPayType] = useState<PayType>(params.get('type') === 'ADVANCE' ? 'ADVANCE' : 'INSTALLMENT');
  const [amountEdit, setAmountEdit] = useState<string | null>(null);
  const [trxId, setTrxId] = useState('');
  const [sender, setSender] = useState('');
  const senderTouched = useRef(false);
  const { user } = useCurrentUser();
  const savedWallet = (user?.bkashWalletNumber ?? '').replace(/\D/g, '').slice(0, 11);
  const [paidAt, setPaidAt] = useState(todayLocal());
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null);
  const [done, setDone] = useState<{ trxId: string; amount: number; paidAt: string } | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  const toSend = payType === 'ADVANCE' ? emi * 2 : emi;
  const amountText = amountEdit ?? (toSend > 0 ? String(toSend) : '');
  const amount = Number(amountText);
  const today = todayLocal();

  const errors: FieldErrors = {
    ...(TRX_RE.test(trxId) ? {} : { trxId: t('bkashPay.trxInvalid') }),
    ...(Number.isFinite(amount) && amount > 0 ? {} : { amount: t('bkashPay.amountInvalid') }),
    ...(SENDER_RE.test(sender) ? {} : { senderNumber: t('bkashPay.senderInvalid') }),
    ...(/^\d{4}-\d{2}-\d{2}$/.test(paidAt) && paidAt <= today ? {} : { paidAt: t('bkashPay.dateInvalid') }),
  };
  const shown = (f: Field) => serverErrors[f] ?? (touched[f] ? errors[f] : undefined);
  const valid = FIELDS.every((f) => !errors[f]);

  // Move focus to the step heading so screen readers and keyboard users land on the new content.
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  // Prefill "number you paid from" with the member's saved bKash wallet, unless they already typed their own.
  useEffect(() => {
    if (savedWallet && !senderTouched.current) setSender(savedWallet);
  }, [savedWallet]);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(null), 2500);
    return () => window.clearTimeout(id);
  }, [copied]);

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(BKASH_PAY_NUMBER);
      setCopied('ok');
    } catch {
      setCopied('fail');
    }
  };

  const pickType = (next: PayType) => {
    setPayType(next);
    setAmountEdit(null);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setServerErrors({});
    setTouched({ trxId: true, amount: true, senderNumber: true, paidAt: true });
    if (!valid || !sub.data) return;
    submitBkash.mutate(
      {
        subscriptionId: sub.data.id,
        amount,
        paymentType: payType,
        trxId,
        senderNumber: sender,
        paidAt,
      },
      {
        onSuccess: (record) => {
          setDone({ trxId: record.trxId ?? trxId, amount: record.amount, paidAt });
          setStep(3);
        },
        onError: (err: unknown) => {
          const p = problemOf(err);
          if (p.status === 404 && p.code === 'FEATURE_DISABLED') {
            setDisabled(true);
            return;
          }
          if (p.status === 409 && p.code === 'DUPLICATE_TRX_ID') {
            setServerErrors({ trxId: t('bkashPay.duplicateTrx') });
            return;
          }
          const mapped: FieldErrors = {};
          for (const item of p.errors) {
            if ((FIELDS as string[]).includes(item.field)) mapped[item.field as Field] = item.message;
          }
          if (Object.keys(mapped).length > 0) {
            setServerErrors(mapped);
            return;
          }
          setFormError(errorMessageOf(err, t('common.error'), t));
        },
      },
    );
  };

  if (lock.locked) return <BookingLockNotice />;

  if (disabled) {
    return (
      <div className="bkp-page" data-testid="bkash-pay-page">
        <Card>
          <div className="bkp-disabled" role="status" data-testid="bkash-disabled">
            <span className="bkp-disabled-icon" aria-hidden>
              <AlertTriangle size={26} />
            </span>
            <h1 className="bkp-heading" ref={headingRef} tabIndex={-1}>
              {t('bkashPay.disabledTitle')}
            </h1>
            <p className="muted">{t('bkashPay.disabledBody')}</p>
            <Link to={PAY_FALLBACK_PATH} className="bkp-link-btn" data-testid="bkash-disabled-cta">
              {t('bkashPay.disabledCta')} <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const stepNode = (n: 1 | 2 | 3, label: string) => (
    <li className={`bkp-step${step === n ? ' is-active' : ''}${step > n ? ' is-done' : ''}`} aria-current={step === n ? 'step' : undefined}>
      <span className="bkp-step-num" aria-hidden>
        {step > n ? <Check size={13} /> : n}
      </span>
      <span>{label}</span>
    </li>
  );

  return (
    <div className="bkp-page" data-testid="bkash-pay-page">
      <header className="bkp-head">
        <h1 className="bkp-title">{t('bkashPay.title')}</h1>
        <p className="muted">{t('bkashPay.subtitle')}</p>
      </header>

      <ol className="bkp-stepper" aria-label={t('bkashPay.title')}>
        {stepNode(1, t('bkashPay.stepSend'))}
        {stepNode(2, t('bkashPay.stepConfirm'))}
        {stepNode(3, t('bkashPay.stepDone'))}
      </ol>

      {step === 1 && (
        <Card>
          <h2 className="bkp-heading" ref={headingRef} tabIndex={-1}>
            {t('bkashPay.sendTitle')}
          </h2>

          <div className="bkp-number-panel">
            <div className="bkp-number-meta">
              <span className="bkp-eyebrow">{t('bkashPay.numberLabel')}</span>
              <Badge tone="neutral">
                {t('bkashPay.mode')}: {BKASH_PAY_MODE}
              </Badge>
            </div>
            <div className="bkp-number-row">
              <span className="bkp-number" data-testid="bkash-number">
                {BKASH_PAY_NUMBER}
              </span>
              <button
                type="button"
                className={`bkp-copy${copied === 'ok' ? ' is-copied' : ''}`}
                onClick={() => void copyNumber()}
                aria-label={t('bkashPay.copy')}
                data-testid="bkash-copy"
              >
                {copied === 'ok' ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
                <span>{copied === 'ok' ? t('bkashPay.copied') : t('bkashPay.copy')}</span>
              </button>
            </div>
            <p className={`bkp-live${copied === 'fail' ? '' : ' is-quiet'}`} role="status" aria-live="polite" data-testid="bkash-copy-status">
              {copied === 'ok' ? t('bkashPay.copied') : copied === 'fail' ? t('bkashPay.copyFailed') : ''}
            </p>
          </div>

          <fieldset className="bkp-type">
            <legend className="field-label">{t('bkashPay.payType')}</legend>
            <div className="bkp-type-row">
              <button
                type="button"
                className={`bkp-type-btn${payType === 'INSTALLMENT' ? ' is-on' : ''}`}
                aria-pressed={payType === 'INSTALLMENT'}
                onClick={() => pickType('INSTALLMENT')}
                data-testid="bkash-type-emi"
              >
                {t('bkashPay.typeEmi')}
              </button>
              <button
                type="button"
                className={`bkp-type-btn${payType === 'ADVANCE' ? ' is-on' : ''}`}
                aria-pressed={payType === 'ADVANCE'}
                onClick={() => pickType('ADVANCE')}
                data-testid="bkash-type-advance"
              >
                {t('bkashPay.typeAdvance')}
              </button>
            </div>
          </fieldset>

          <div className="bkp-amount-box">
            <span className="bkp-eyebrow">{t('bkashPay.amountToSend')}</span>
            <strong className="bkp-amount" data-testid="bkash-amount-to-send">
              {formatBDT(toSend, locale)}
            </strong>
          </div>

          <h3 className="bkp-sub">{t('bkashPay.howTitle')}</h3>
          <ol className="bkp-how">
            {(['how1', 'how2', 'how3', 'how4', 'how5'] as const).map((k) => (
              <li key={k}>{t(`bkashPay.${k}`)}</li>
            ))}
          </ol>

          {sub.isNoSubscription && (
            <p className="text-danger" role="alert">
              {t('bkashPay.noSubscription')}
            </p>
          )}

          <Button
            className="bkp-cta"
            onClick={() => setStep(2)}
            disabled={sub.isNoSubscription || toSend <= 0}
            data-testid="bkash-continue"
          >
            <span>{t('bkashPay.iPaid')}</span>
            <ArrowRight size={16} aria-hidden />
          </Button>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <h2 className="bkp-heading" ref={headingRef} tabIndex={-1}>
            {t('bkashPay.confirmTitle')}
          </h2>
          <p className="muted bkp-intro">{t('bkashPay.confirmHint')}</p>
          <form className="stack" onSubmit={submit} noValidate>
            <Input
              id="bkp-trx"
              label={t('bkashPay.trxLabel')}
              value={trxId}
              onChange={(e) => {
                setTrxId(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12));
                setServerErrors((s) => ({ ...s, trxId: undefined }));
              }}
              onBlur={() => setTouched((s) => ({ ...s, trxId: true }))}
              error={shown('trxId')}
              hint={t('bkashPay.trxHint')}
              placeholder="9H7K2LMNQ4"
              autoCapitalize="characters"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              maxLength={12}
              className="bkp-trx-field"
              required
              data-testid="bkash-trx"
            />
            <Input
              id="bkp-amount"
              label={t('bkashPay.amountLabel')}
              type="number"
              inputMode="decimal"
              min={1}
              value={amountText}
              onChange={(e) => {
                setAmountEdit(e.target.value);
                setServerErrors((s) => ({ ...s, amount: undefined }));
              }}
              onBlur={() => setTouched((s) => ({ ...s, amount: true }))}
              error={shown('amount')}
              required
              data-testid="bkash-amount"
            />
            <Input
              id="bkp-sender"
              label={t('bkashPay.senderLabel')}
              type="tel"
              inputMode="numeric"
              value={sender}
              onChange={(e) => {
                senderTouched.current = true;
                setSender(e.target.value.replace(/\D/g, '').slice(0, 11));
                setServerErrors((s) => ({ ...s, senderNumber: undefined }));
              }}
              onBlur={() => setTouched((s) => ({ ...s, senderNumber: true }))}
              error={shown('senderNumber')}
              hint={t('bkashPay.senderHint')}
              placeholder="01XXXXXXXXX"
              autoComplete="tel-national"
              required
              data-testid="bkash-sender"
            />
            <Input
              id="bkp-date"
              label={t('bkashPay.dateLabel')}
              type="date"
              value={paidAt}
              max={today}
              onChange={(e) => {
                setPaidAt(e.target.value);
                setServerErrors((s) => ({ ...s, paidAt: undefined }));
              }}
              onBlur={() => setTouched((s) => ({ ...s, paidAt: true }))}
              error={shown('paidAt')}
              required
              data-testid="bkash-date"
            />

            {formError && (
              <p className="text-danger" role="alert" data-testid="bkash-form-error">
                {formError}
              </p>
            )}

            <div className="bkp-actions">
              <Button variant="secondary" onClick={() => setStep(1)} disabled={submitBkash.isPending} data-testid="bkash-back">
                <ArrowLeft size={16} aria-hidden />
                <span>{t('bkashPay.back')}</span>
              </Button>
              <Button type="submit" loading={submitBkash.isPending} data-testid="bkash-submit">
                <span>{t('bkashPay.submit')}</span>
              </Button>
            </div>
          </form>
        </Card>
      )}

      {step === 3 && done && (
        <Card>
          <div className="bkp-success" data-testid="bkash-success">
            <span className="bkp-success-icon" aria-hidden>
              <CheckCircle2 size={34} />
            </span>
            <Badge tone="warning">{t('payments.pendingVerification')}</Badge>
            <h2 className="bkp-heading" ref={headingRef} tabIndex={-1}>
              {t('bkashPay.successTitle')}
            </h2>
            <p className="muted">{t('bkashPay.successBody')}</p>
          </div>

          <dl className="kv bkp-summary" aria-label={t('bkashPay.summary')}>
            <dt>{t('bkashPay.summaryTrx')}</dt>
            <dd className="bkp-mono" data-testid="bkash-done-trx">
              {done.trxId}
            </dd>
            <dt>{t('bkashPay.summaryAmount')}</dt>
            <dd data-testid="bkash-done-amount">{formatBDT(done.amount, locale)}</dd>
            <dt>{t('bkashPay.summaryDate')}</dt>
            <dd data-testid="bkash-done-date">{done.paidAt}</dd>
          </dl>

          <h3 className="bkp-sub">{t('bkashPay.nextTitle')}</h3>
          <ul className="bkp-next">
            {(['next1', 'next2', 'next3'] as const).map((k) => (
              <li key={k}>
                <ShieldCheck size={15} aria-hidden />
                <span>{t(`bkashPay.${k}`)}</span>
              </li>
            ))}
          </ul>

          <div className="bkp-actions">
            <Button onClick={() => navigate('/payments')} data-testid="bkash-to-history">
              {t('bkashPay.toHistory')}
            </Button>
            <Button variant="secondary" onClick={() => navigate('/')} data-testid="bkash-to-dashboard">
              {t('bkashPay.toDashboard')}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
